// stores/Auth.ts
import { defineStore } from 'pinia'
import AuthService from '@/services/auth.service'
import AdminService from '@/services/admin.service'
import { api } from '@/services/api.service';
import {withLoader} from "@/utils/helpers.ts";
import { usePostHog } from '@/app/composables/usePostHog';

const identifyPostHogUser = (user: any) => {
    if (!user?.id) return;

    const { identify } = usePostHog();
    identify(user.id, {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        user_type: user.type,
    });
};

const resetPostHogUser = () => {
    const { reset } = usePostHog();
    reset();
};

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: '' as string,
        user: null as any,
    }),

    actions: {
        async loginAs(userId: number) {
            await withLoader(async () => {
                const response = await AdminService.loginAsUser(userId);
                if (response.data.token && response.data.user) {
                    this.setCurrentUser(response.data.user, response.data.token);
                }
            });
        },
        
        setCurrentUser(user: {}, token: string) {
            this.clearSessionState();
            api.defaults.headers.common['Authorization'] = 'Bearer ' + token;
            this.token = token
            this.user = user
            localStorage.setItem('CURRENT_USER', JSON.stringify(this.user))
            localStorage.setItem('CURRENT_TOKEN', this.token)
            identifyPostHogUser(this.user)
        },
        identifyCurrentUser() {
            identifyPostHogUser(this.user)
        },
        clearSessionState() {
            this.token = ''
            this.user = null
            localStorage.removeItem('CURRENT_USER')
            localStorage.removeItem('CURRENT_TOKEN')
            localStorage.removeItem('CONVEX_JWT_CACHE')
            delete api.defaults.headers.common['Authorization'];
        },
        clearCurrentUser() {
            this.clearSessionState();
            resetPostHogUser();
        },
        logout() {
            this.clearCurrentUser();
        },
        async switchToOldDashboard() {
            return await AuthService.switchToOldDashboard();
        },
        async forgotPassword(email: string) {
            return await withLoader( async () => {
                return await AuthService.forgotPassword(email);
            })
        },
        async resetPassword( payload: { email: string; token: string; password: string; password_confirmation: string } ) {
            return await withLoader( async () => {
                return await AuthService.resetPassword(payload);
            })
        },
        async login(email: string, password: string) {
            return await withLoader(async () => {
                const response = await AuthService.login({ email, password })
                if (response.data.status && response.data.user && response.data.token) {
                    this.setCurrentUser(response.data.user, response.data.token);
                    return response.data
                }
                throw new Error('Login failed')
            })
        },
    }
})
